import React, { useState } from 'react';
import {
  Paper,
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Chip,
  Stack,
  Divider,
  Link as MuiLink
} from '@mui/material';
import {
  Work as WorkIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Link as LinkIcon
} from '@mui/icons-material';

/**
 * JobInfoSection - Tabbed section for Job Details, Company Insights, and Original Job Description
 * 
 * Tabs:
 * - Job Details: Position, company, pay, location, employment info
 * - Company Insights: Notes, contacts, research (Coming Soon)
 * - Original Job Description: Full raw text from job posting
 */
const JobInfoSection = ({ application, jobDescription }) => {
  const [currentTab, setCurrentTab] = useState('details');

  if (!application?.job_description) {
    return null;
  }

  const jd = application.job_description;
  const structuredData = jobDescription?.structured_data || jd;

  // Format salary range
  const formatSalary = (min, max) => {
    if (!min && !max) return null;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    });
    
    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}`;
    } else if (min) {
      return `${formatter.format(min)}+`;
    } else {
      return `Up to ${formatter.format(max)}`;
    }
  };

  const salaryRange = formatSalary(jd.salary_min, jd.salary_max);

  return (
    <Paper elevation={1}>
      {/* Tab Headers */}
      <Tabs
        value={currentTab}
        onChange={(e, newValue) => setCurrentTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="details" label="Job Details" />
        <Tab value="insights" label="Company Insights" />
        <Tab value="original" label="Original Job Description" />
      </Tabs>

      {/* Tab Content */}
      <Box>
        {/* Job Details Tab */}
        {currentTab === 'details' && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Column 1: Position, Company & Location Info */}
              <Grid item xs={12} md={6}>
                {/* Position */}
                {jd.position && (
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <WorkIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Position
                      </Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight="medium">
                      {jd.position}
                    </Typography>
                  </Box>
                )}

                {/* Company */}
                <Box mb={2}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Company
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight="medium">
                    {jd.company || 'Unknown Company'}
                  </Typography>
                </Box>

                {/* Location */}
                {jd.location && (
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {jd.location}
                    </Typography>
                  </Box>
                )}

                {/* Work Arrangement */}
                {jd.work_arrangement && (
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Work Arrangement
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {jd.work_arrangement}
                    </Typography>
                  </Box>
                )}

                {/* Employment Type */}
                {jd.employment_type && (
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <WorkIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Employment Type
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {jd.employment_type}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Column 2: Compensation & Level */}
              <Grid item xs={12} md={6}>
                {/* Salary Range */}
                {salaryRange && (
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <MoneyIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Pay Range
                      </Typography>
                    </Stack>
                    <Typography variant="body1" color="success.main" fontWeight="medium">
                      {salaryRange}
                    </Typography>
                  </Box>
                )}

                {/* Experience Level */}
                {jd.experience_level && (
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <TrendingUpIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Experience Level
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {jd.experience_level}
                    </Typography>
                  </Box>
                )}

                {/* Application URL */}
                {jd.application_url && (
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Application Link
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      component="a"
                      href={jd.application_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      View original posting →
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Requirements Summary */}
              {jd.requirements && jd.requirements.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Key Requirements ({jd.requirements.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {jd.requirements.slice(0, 10).map((req, index) => (
                      <Chip
                        key={index}
                        label={req}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {jd.requirements.length > 10 && (
                      <Chip
                        label={`+${jd.requirements.length - 10} more`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    )}
                  </Box>
                </Grid>
              )}

              {/* Qualifications Summary */}
              {jd.qualifications && jd.qualifications.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Preferred Qualifications ({jd.qualifications.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {jd.qualifications.slice(0, 10).map((qual, index) => (
                      <Chip
                        key={index}
                        label={qual}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    ))}
                    {jd.qualifications.length > 10 && (
                      <Chip
                        label={`+${jd.qualifications.length - 10} more`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Company Insights Tab */}
        {currentTab === 'insights' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Company Insights - Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This section will include notes, contacts, and company research.
            </Typography>
          </Box>
        )}

        {/* Original Job Description Tab */}
        {currentTab === 'original' && (
          <Box sx={{ p: 3 }}>
            {jobDescription?.raw_text ? (
              <>
                {/* Metadata - URL and timestamp */}
                <Box mb={3}>
                  {jobDescription.url && (
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <LinkIcon fontSize="small" color="action" />
                      <MuiLink
                        href={jobDescription.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        {jobDescription.url}
                      </MuiLink>
                    </Stack>
                  )}
                  {jobDescription.metadata?.fetched_at && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Fetched: {new Date(jobDescription.metadata.fetched_at).toLocaleString()}
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Raw job description text */}
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    maxHeight: '70vh',
                    overflow: 'auto'
                  }}
                >
                  {jobDescription.raw_text}
                </Typography>
              </>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Original Job Description Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The raw job description text was not saved for this application.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default JobInfoSection;
